import React, { useEffect, useRef, useState } from 'react';
import axios from '../../../api/axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { updateTemplateDetailsRoute, createTemplateRoute, uploadTemplateImageRoute, getSiteAdminTemplateCategoriesRoute, exportTemplateRoute, importTemplateRoute } from '../../../api/routes';
import { TOP_LEVEL_NAV_ROUTES } from '../../../config/NavRoutes';
import FormField from '../../../components/forms/FormField/FormField';
import TextArea from '../../../components/forms/TextArea/TextArea';
import ImageUploader, { UPLOADER_IMAGE_SIDE } from '../../../components/forms/ImageUpload/ImageUploader';
import AdvancedDropDownList from '../../../components/forms/DropDownList/AdvancedDropDownList';
import FormDropDown from '../../../components/forms/DropDownList/FormDropDown';
import ClosePanelBtn from '../../../components/ClosePanelBtn/ClosePanelBtn';
import toDisplayAssetUrl from '../../../helpers/DisplayAssetUrl';
import { getTemplateCategoryIds } from '../../../helpers/templateCategories';
import '../SiteAdmin.scss';

const EditTemplate = ({
    template,
    isNew = false,
    onBack,
    onSaved,
    initialTemplateType,
    lockTemplateType = false,
    createButtonLabel,
    createCampaignOnSave = false,
    showAttachedCampaignPanel = true,
    headerTitle,
    isModal = false,
}) => {
    const navigate = useNavigate();
    const currentTemplateId = template.id || template._id || '';
    const [name, setName] = useState(template.name || '');
    const [description, setDescription] = useState(template.description || '');
    const [templateImage, setTemplateImage] = useState(template.templateImage || '');
    const [selectedCategoryIds, setSelectedCategoryIds] = useState(getTemplateCategoryIds(template));
    const [templateType, setTemplateType] = useState(initialTemplateType || template.templateType || 'feature');
    const [isActive, setIsActive] = useState(template.isActive === true);
    const [isQuickstart, setIsQuickstart] = useState(!!template.isQuickstart);
    const [isFeatured, setIsFeatured] = useState(!!template.isFeatured);
    const [categories, setCategories] = useState([]);
    const [saving, setSaving] = useState(false);
    const [creatingCampaign, setCreatingCampaign] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState(template);
    const importInputRef = useRef(null);
    const resolvedTemplateId = currentTemplate.id || currentTemplate._id || currentTemplateId;
    const uploadTargetId = resolvedTemplateId;
    const uploadTemplateImageEndpoint = uploadTargetId
        ? `${uploadTemplateImageRoute}?id=${encodeURIComponent(uploadTargetId)}`
        : uploadTemplateImageRoute;

    const hasMultipleFeatures = currentTemplate.stepFeatureCount != null
        ? currentTemplate.stepFeatureCount > 1
        : (currentTemplate.features && currentTemplate.features.length > 1);

    useEffect(() => {
        setCurrentTemplate(template);
        setName(template.name || '');
        setDescription(template.description || '');
        setTemplateImage(template.templateImage || '');
        setSelectedCategoryIds(getTemplateCategoryIds(template));
        setTemplateType(initialTemplateType || template.templateType || 'feature');
        setIsActive(template.isActive === true);
        setIsQuickstart(!!template.isQuickstart);
        setIsFeatured(!!template.isFeatured);
    }, [template, initialTemplateType]);

    useEffect(() => {
        if (initialTemplateType) {
            setTemplateType(initialTemplateType);
        }
    }, [initialTemplateType]);

    useEffect(() => {
        axios.get(getSiteAdminTemplateCategoriesRoute, { withCredentials: true })
            .then(res => {
                if (res.data.categories) setCategories(res.data.categories);
            })
            .catch(() => toast.error('Failed to load categories'));
    }, []);

    const categoryMap = categories.reduce((acc, category) => {
        acc[category.id] = category;
        return acc;
    }, {});

    const selectedCategories = selectedCategoryIds
        .map((categoryId) => categoryMap[categoryId])
        .filter(Boolean);

    const categoryOptions = [...categories]
        .sort((a, b) => {
            const aSelected = selectedCategoryIds.includes(a.id);
            const bSelected = selectedCategoryIds.includes(b.id);
            if (aSelected !== bSelected) return aSelected ? -1 : 1;
            if ((a.isActive === true) !== (b.isActive === true)) return a.isActive === true ? -1 : 1;
            return (a.name || '').localeCompare(b.name || '');
        })
        .map((category) => ({
            ...category,
            _id: category.id,
            label: category.isActive === true ? category.name : `${category.name} (Inactive)`,
            name: category.name,
        }));

    const categorySummary = (() => {
        if (selectedCategories.length === 0) return 'Select categories';
        if (selectedCategories.length <= 2) return selectedCategories.map((category) => category.name).join(', ');
        const firstTwo = selectedCategories.slice(0, 2).map((category) => category.name).join(', ');
        return `${firstTwo} +${selectedCategories.length - 2}`;
    })();

    const toggleCategorySelection = (nextCategoryId) => {
        setSelectedCategoryIds((prev) => (
            prev.includes(nextCategoryId)
                ? prev.filter((categoryId) => categoryId !== nextCategoryId)
                : [...prev, nextCategoryId]
        ));
    };

    const handleSave = async () => {
        if (!name.trim()) return toast.error('Name is required');
        if (!isNew && selectedCategoryIds.length === 0) return toast.error('At least one category is required');

        const primaryCategoryId = selectedCategoryIds[0] || undefined;

        setSaving(true);
        try {
            let res;
            if (isNew) {
                res = await axios.post(
                    createTemplateRoute,
                    {
                        name: name.trim(),
                        description: description.trim(),
                        templateImage: templateImage.trim(),
                        categoryId: primaryCategoryId,
                        categoryIds: selectedCategoryIds,
                        templateType,
                    },
                    { withCredentials: true }
                );
            } else {
                res = await axios.put(
                    `${updateTemplateDetailsRoute}/${resolvedTemplateId}`,
                    {
                        name: name.trim(),
                        description: description.trim(),
                        templateImage: templateImage.trim(),
                        categoryId: primaryCategoryId,
                        categoryIds: selectedCategoryIds,
                        templateType,
                        isActive,
                        isQuickstart,
                        isFeatured,
                    },
                    { withCredentials: true }
                );
            }
            if (res.data.success) {
                let nextTemplate = res.data.template;

                if (isNew && createCampaignOnSave && nextTemplate?.id) {
                    const campaignRes = await axios.post(
                        `${updateTemplateDetailsRoute}/${nextTemplate.id}/create-campaign`,
                        {},
                        { withCredentials: true }
                    );

                    if (!campaignRes.data.success) {
                        throw new Error('Failed to create campaign');
                    }

                    nextTemplate = campaignRes.data.template;
                }

                toast.success(isNew ? 'Template created!' : 'Template saved');
                setCurrentTemplate(nextTemplate);
                onSaved(nextTemplate, { openCampaignEditor: Boolean(isNew && createCampaignOnSave) });
            }
        } catch {
            toast.error(isNew ? 'Failed to create template' : 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    const handleCreateCampaign = async () => {
        if (!resolvedTemplateId) return;
        setCreatingCampaign(true);
        try {
            const res = await axios.post(
                `${updateTemplateDetailsRoute}/${resolvedTemplateId}/create-campaign`,
                {},
                { withCredentials: true }
            );
            if (res.data.success) {
                toast.success('Campaign created and linked!');
                setCurrentTemplate(res.data.template);
                onSaved(res.data.template, { stayOnPage: true });
            }
        } catch {
            toast.error('Failed to create campaign');
        } finally {
            setCreatingCampaign(false);
        }
    };

    const handleDownloadTemplate = async () => {
        if (!resolvedTemplateId) return;

        setDownloading(true);
        try {
            const res = await axios.get(`${exportTemplateRoute}/${resolvedTemplateId}/export`, {
                withCredentials: true,
                responseType: 'blob',
            });

            const contentDisposition = res.headers['content-disposition'] || '';
            const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
            const filename = filenameMatch?.[1] || `${(currentTemplate.name || 'template').replace(/[^a-z0-9-_]+/gi, '-')}.zip`;
            const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch {
            toast.error('Failed to download template');
        } finally {
            setDownloading(false);
        }
    };

    const handleImportTemplateClick = () => {
        if (importing) return;
        importInputRef.current?.click();
    };

    const handleImportTemplate = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        setImporting(true);
        try {
            const formData = new FormData();
            formData.append('templatePackage', file);

            const res = await axios.post(importTemplateRoute, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (!res.data.success || !res.data.template) {
                throw new Error('Import failed');
            }

            toast.success('Template loaded');
            onSaved(res.data.template, { openCampaignEditor: Boolean(res.data.template.campaignId) });
        } catch {
            toast.error('Failed to load template');
        } finally {
            setImporting(false);
        }
    };

    const campaignId = currentTemplate.campaignId ? currentTemplate.campaignId.toString() : null;

    return (
        <div className={`edit-template-view${isModal ? ' edit-template-view--modal' : ''}`}>
            <div className="edit-template-header">
                {!isModal && <span className="et-back-btn" onClick={onBack} title="Back">{'<'}</span>}
                <h5 className="edit-template-title">
                    {headerTitle || (isNew ? 'Create Template' : 'Edit Template')}
                    {!isNew && <span className="edit-template-subtitle">{currentTemplate.featureType}</span>}
                </h5>
                {isModal && <ClosePanelBtn clickFunc={onBack} />}
            </div>

            <div className="edit-template-body">
                <div className="site-admin-panel edit-template-panel">
                    <div className="site-admin-panel-title">Template Details</div>

                    <ImageUploader
                        label="Template Image"
                        id={uploadTargetId}
                        currentImg={toDisplayAssetUrl(templateImage)}
                        uploadImmediately={true}
                        endPoint={uploadTemplateImageEndpoint}
                        uploadBtnLabel="Upload Image"
                        showRemoveOption={false}
                        previewSide={UPLOADER_IMAGE_SIDE.right}
                        previewWidth={160}
                        previewHeight={90}
                        uploadedCallback={(res) => {
                            if (res.data.url) setTemplateImage(res.data.url);
                        }}
                        uploadErrorCallback={() => toast.error('Image upload failed')}
                    />

                    <div className="et-image-actions">
                        {templateImage && (
                            <div
                                className="et-clear-image"
                                onClick={() => setTemplateImage('')}
                            >
                                Clear Image
                            </div>
                        )}
                    </div>
                    <p className="et-help-text">Image is optional.</p>

                    {categories.length > 0 && (
                        <div className="et-field">
                            <div className="et-label">Categories</div>
                            <AdvancedDropDownList
                                id="templateCategories"
                                items={categoryOptions}
                                selectedItems={categoryOptions.filter((category) => selectedCategoryIds.includes(category.id))}
                                onSelect={(category) => toggleCategorySelection(category.id)}
                                summary={categorySummary}
                                placeholder="Select categories"
                                className="et-category-dropdown"
                                selectedClass="default-selected"
                                deselectedClass="default-selected"
                                enabled={true}
                            />
                            <p className="et-help-text">Templates can live in more than one category.</p>
                        </div>
                    )}

                    <div className="et-compact-grid">
                        <FormField
                            showLabel={true}
                            label="Name"
                            type="text"
                            id="et-name"
                            placeholder="Template name"
                            value={name}
                            setFunc={setName}
                        />

                        {hasMultipleFeatures ? (
                            <div className="et-field">
                                <div className="et-label">Template Type</div>
                                <p className="bold-text indented">
                                    Campaign Template
                                    <span style={{ display: 'block', fontSize: '0.75rem', fontStyle: 'italic', fontWeight: 'normal', color: 'var(--label-color)', marginTop: '0.2rem' }}>
                                        Forced — multiple features in campaign
                                    </span>
                                </p>
                            </div>
                        ) : lockTemplateType ? (
                            <div className="et-field">
                                <div className="et-label">Template Type</div>
                                <p className="bold-text indented">{templateType === 'campaign' ? 'Campaign Template' : 'Feature Template'}</p>
                            </div>
                        ) : (
                            <FormDropDown
                                label="Template Type"
                                showLabel={true}
                                id="et-template-type"
                                items={[
                                    { label: 'Feature Template', value: 'feature' },
                                    { label: 'Campaign Template', value: 'campaign' },
                                ]}
                                currentIndex={templateType === 'campaign' ? 1 : 0}
                                selectFunc={(e, id, item) => setTemplateType(item.value)}
                                enabled={true}
                            />
                        )}
                    </div>

                    <TextArea
                        label="Description"
                        id="et-description"
                        placeholder="Template description"
                        value={description}
                        setFunc={setDescription}
                        rows={3}
                    />

                    <div className="et-field et-flag-group">
                        <div className="et-label">Visibility</div>
                        <div className="et-visibility-row">
                            <div className="tm-flag">
                                <span>Active</span>
                                <div
                                    className={`tm-flag-toggle${isActive ? ' on' : ''}`}
                                    onClick={() => setIsActive(v => !v)}
                                    style={{ cursor: 'pointer' }}
                                    title={isActive ? 'Click to deactivate' : 'Click to activate'}
                                />
                            </div>

                            <div className="tm-flag">
                                <span>Quickstart</span>
                                <div
                                    className={`tm-flag-toggle${isQuickstart ? ' on' : ''}`}
                                    onClick={() => setIsQuickstart(v => !v)}
                                    style={{ cursor: 'pointer' }}
                                    title={isQuickstart ? 'Remove from quickstart' : 'Mark as quickstart'}
                                />
                            </div>

                            <div className="tm-flag">
                                <span>Featured</span>
                                <div
                                    className={`tm-flag-toggle${isFeatured ? ' on' : ''}`}
                                    onClick={() => setIsFeatured(v => !v)}
                                    style={{ cursor: 'pointer' }}
                                    title={isFeatured ? 'Remove from featured' : 'Mark as featured'}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="et-actions">
                        {isNew && (
                            <>
                                <input
                                    ref={importInputRef}
                                    type="file"
                                    accept=".zip,application/zip"
                                    style={{ display: 'none' }}
                                    onChange={handleImportTemplate}
                                />
                                <div
                                    className={`standard-button button-active sa-save-btn${importing ? ' button-inactive' : ''}`}
                                    onClick={importing ? undefined : handleImportTemplateClick}
                                >
                                    {importing ? 'Loading…' : 'Load Template'}
                                </div>
                            </>
                        )}

                        {!isNew && (
                            <div
                                className={`standard-button button-active sa-save-btn${downloading ? ' button-inactive' : ''}`}
                                onClick={downloading ? undefined : handleDownloadTemplate}
                            >
                                {downloading ? 'Preparing…' : 'Download Template'}
                            </div>
                        )}

                        <div
                            className={`standard-button button-active sa-save-btn${saving ? ' button-inactive' : ''}`}
                            onClick={saving ? undefined : handleSave}
                        >
                            {saving ? 'Saving…' : (createButtonLabel || (isNew ? 'Create Template' : 'Save Changes'))}
                        </div>
                    </div>
                </div>

                {showAttachedCampaignPanel && (
                <div className="site-admin-panel edit-template-panel">
                    <div className="site-admin-panel-title">Attached Campaign</div>
                    {campaignId ? (
                        <div className="et-campaign-row">
                            <span className="et-campaign-label">
                                Campaign ID: <code className="et-campaign-id">{campaignId}</code>
                            </span>
                            <span
                                className="et-campaign-link"
                                onClick={() =>
                                    navigate(TOP_LEVEL_NAV_ROUTES.CAMPAIGN_OVERVIEW.replace(':id', campaignId))
                                }
                            >
                                View Campaign →
                            </span>
                        </div>
                    ) : resolvedTemplateId ? (
                        <div>
                            <p className="et-no-campaign">No campaign attached yet.</p>
                            <div
                                className={`standard-button button-active sa-save-btn${creatingCampaign ? ' button-inactive' : ''}`}
                                onClick={creatingCampaign ? undefined : handleCreateCampaign}
                            >
                                {creatingCampaign ? 'Creating…' : 'Create Campaign'}
                            </div>
                        </div>
                    ) : (
                        <span className="et-no-campaign">Save the template first, then create a campaign.</span>
                    )}
                </div>
                )}
            </div>
        </div>
    );
};

export default EditTemplate;
